import { MessageSquare, Settings, ChevronLeft, ChevronRight, LogOut, BarChart3, ClipboardList, Users, UserCheck, Package, CreditCard, CalendarDays, Repeat, Megaphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from '@/components/NavLink';
import { useAuthContext } from '@/contexts/AuthContext';
import { useConversasNaoLidas } from '@/hooks/useConversasNaoLidas';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { getOrgInitials } from '@/lib/org-branding';

interface MenuItem {
  titleKey: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  prestadorOnly?: boolean;
  adminOnly?: boolean;
  ocultarPrestador?: boolean;
  /**
   * Quando true, o link só fica activo na rota exacta — evita que `/foo`
   * apareça activo quando estamos em `/foo/bar`. Usado para `/configuracoes`
   * que tem uma sub-rota `/configuracoes/plano`.
   */
  exact?: boolean;
}

// Itens de operações (dia a dia)
const operacoesItems: MenuItem[] = [
  { titleKey: 'nav.dashboard', url: '/dashboard', icon: BarChart3, ocultarPrestador: true },
  { titleKey: 'nav.panel', url: '/painel', icon: MessageSquare, ocultarPrestador: true },
  { titleKey: 'nav.clients', url: '/clientes', icon: UserCheck, ocultarPrestador: true },
  { titleKey: 'nav.orders', url: '/ordens', icon: ClipboardList, ocultarPrestador: true },
  { titleKey: 'nav.recurrences', url: '/recorrencias', icon: Repeat, ocultarPrestador: true },
  { titleKey: 'nav.agenda', url: '/agenda', icon: CalendarDays, ocultarPrestador: true },
  { titleKey: 'nav.requests', url: '/solicitacoes', icon: Megaphone, ocultarPrestador: true },
];

// Itens de administração
const adminItems: MenuItem[] = [
  { titleKey: 'nav.team', url: '/equipe', icon: Users, adminOnly: true },
  { titleKey: 'nav.services', url: '/servicos', icon: Package, adminOnly: true },
  { titleKey: 'nav.plan', url: '/configuracoes/plano', icon: CreditCard, adminOnly: true },
];

// Settings vive no rodapé da sidebar, acima do botão "Sair".
// `exact: true` para não casar com `/configuracoes/plano` (rota irmã).
const settingsItem: MenuItem = {
  titleKey: 'nav.settings',
  url: '/configuracoes',
  icon: Settings,
  adminOnly: true,
  exact: true,
};

// Configurações do prestador vivem no rodapé, acima do "Sair" — espelho do
// settingsItem do admin. (O perfil/foto vive em /meu-perfil, acedido pelo nome.)
const prestadorSettingsItem: MenuItem = {
  titleKey: 'nav.settings',
  url: '/configuracoes-prestador',
  icon: Settings,
  prestadorOnly: true,
};

// Itens apenas para prestadores
const prestadorItems: MenuItem[] = [
  { titleKey: 'nav.myServices', url: '/meus-servicos', icon: ClipboardList, prestadorOnly: true },
  { titleKey: 'nav.agenda', url: '/minha-agenda', icon: CalendarDays, prestadorOnly: true },
];

export function AppSidebar() {
  const { state, toggleSidebar } = useSidebar();
  const { isAdmin, isPrestador, signOut, organization } = useAuthContext();
  const { t } = useTranslation();
  const collapsed = state === 'collapsed';
  const conversasNaoLidas = useConversasNaoLidas();

  const handleLogout = async () => {
    await signOut();
  };

  const renderMenuItem = (item: MenuItem) => {
    // Esconder configurações e prestadores para não-admins
    if (item.adminOnly && !isAdmin) return null;
    // Esconder meus serviços para não-prestadores
    if (item.prestadorOnly && !isPrestador) return null;
    // Ocultar itens para prestadores
    if (item.ocultarPrestador && isPrestador) return null;

    return (
      <SidebarMenuItem key={item.titleKey}>
        <SidebarMenuButton asChild>
          <NavLink
            to={item.url}
            end={item.exact}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-full px-3 py-2 text-muted-foreground font-medium transition-colors hover:bg-muted hover:text-foreground`}
            activeClassName="bg-foreground text-background font-semibold shadow-pill hover:bg-foreground hover:text-background"
          >
            <span className="relative flex-shrink-0">
              <item.icon className="h-5 w-5" />
              {item.url === '/painel' && conversasNaoLidas > 0 && collapsed && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-foreground" aria-hidden />
              )}
            </span>
            {!collapsed && <span className="flex-1">{t(item.titleKey)}</span>}
            {item.url === '/painel' && conversasNaoLidas > 0 && !collapsed && (
              <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-foreground text-background text-xs font-semibold flex items-center justify-center">
                {conversasNaoLidas > 99 ? '99+' : conversasNaoLidas}
              </span>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  // Verifica se há itens visíveis em cada grupo
  const hasOperacoes = !isPrestador;
  const hasAdmin = isAdmin;
  const hasPrestador = isPrestador;

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className={collapsed ? 'p-2 border-b border-border' : 'px-4 pt-8 pb-7 border-b border-border'}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-full h-8 w-8 bg-primary">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.nome ?? ''}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-primary-foreground font-bold text-sm">
                  {getOrgInitials(organization?.nome)}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="relative flex flex-col items-center pt-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="absolute right-0 top-0 h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-full h-20 w-20 bg-primary">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.nome ?? ''}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-primary-foreground font-bold text-2xl">
                  {getOrgInitials(organization?.nome)}
                </span>
              )}
            </div>
            {organization?.nome && (
              <span className="mt-2 max-w-full truncate text-center text-sm font-semibold text-foreground">
                {organization.nome}
              </span>
            )}
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="pt-3">
        {/* Grupo único: junta operações + admin (sem labels nem separador). */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {hasOperacoes && operacoesItems.map(renderMenuItem)}
              {hasPrestador && prestadorItems.map(renderMenuItem)}
              {hasAdmin && adminItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* User identity moved to the UserMenu in each page's PageHeader
          (top-right). Footer hosts Settings + Logout so they live together
          at the bottom of the rail. */}
      <SidebarFooter className={collapsed ? 'p-2' : 'p-4'}>
        <SidebarMenu>
          {isAdmin && renderMenuItem(settingsItem)}
          {isPrestador && renderMenuItem(prestadorSettingsItem)}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <button
                type="button"
                onClick={handleLogout}
                aria-label={t('nav.logout')}
                className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-full px-3 py-2 text-muted-foreground font-medium transition-colors hover:bg-muted hover:text-foreground`}
              >
                <LogOut className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span>{t('nav.logout')}</span>}
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
